import aiAPI from '@edulastic/api/src/aiAPI'
import { testItemsApi, segmentApi } from '@edulastic/api'
import { get, uniq, chunk, omit } from 'lodash'
import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { notification } from '@edulastic/common'
import { statusConstants } from '@edulastic/constants/const/test'
import {
  applyAIMetadataToQuestion,
  getEligibleTestMetadataQuestions,
} from '../utils/AIQuestionAssistance'
import {
  getTestItemsSelector,
  getTestEntitySelector,
  receiveTestByIdAction,
} from '../../TestPage/ducks'
import { getCurriculumsListSelector } from '../../src/selectors/dictionaries'
import { getUserIdSelector } from '../../src/selectors/user'

export const namespaceKey = 'aiBulkQuestionMetadata'

export const BULK_METADATA_BATCH_SIZE = 20

export const BULK_ITEM_STATUS = {
  GENERATING: 'GENERATING',
  SUCCESS: 'SUCCESS',
  SAVING: 'SAVING',
  ERROR: 'ERROR',
}

const QUESTION_SAVE_FIELDS_TO_OMIT = ['qLabel', 'qSubLabel', 'barLabel']

// Denormalized/read-only fields on test.itemGroups items not allowed by PUT /testitem Joi schema.
const TEST_ITEM_SAVE_FIELDS_TO_OMIT = [
  '__v',
  'previousTestItemId',
  'public',
  'analytics',
  'algoVariablesEnabled',
  'sharedType',
  'sharedWith',
  'createdAt',
  'updatedAt',
  'passageContent',
  'passageTitle',
  'passageLexileValue',
  'passageItemsCount',
  'passageFleschKincaid',
  'passageSource',
  'previousTestItemId',
  'passageData',
  'groupId',
  'isLimitedDeliveryType',
  'groupPassageId',
]

const buildUpdatedItemForSave = (item, updatedQuestions) => ({
  ...omit(item, TEST_ITEM_SAVE_FIELDS_TO_OMIT),
  data: { ...item.data, questions: updatedQuestions },
})

// action constants
export const SET_BULK_STANDARDS_SELECTION = `[${namespaceKey}] set selection`
export const GENERATE_BULK_STANDARDS = `[${namespaceKey}] generate`
export const SET_BULK_STANDARDS_STATE = `[${namespaceKey}] set state`
export const SET_BULK_ITEM_STATUS = `[${namespaceKey}] set item status`
export const SET_BULK_SUGGESTIONS = `[${namespaceKey}] set suggestions`
export const UPDATE_BULK_SUGGESTION = `[${namespaceKey}] update suggestion`
export const SAVE_ITEM_STANDARD = `[${namespaceKey}] save item standard`
export const SAVE_ALL_ITEM_STANDARDS = `[${namespaceKey}] save all item standards`
export const CLEAR_BULK_ITEM = `[${namespaceKey}] clear item`
export const RESET_BULK_STANDARDS_STATE = `[${namespaceKey}] reset state`

// actions
export const setBulkStandardsSelectionAction = (payload) => ({
  type: SET_BULK_STANDARDS_SELECTION,
  payload,
})

export const generateBulkStandardsAction = (payload) => ({
  type: GENERATE_BULK_STANDARDS,
  payload,
})

export const setBulkStandardsStateAction = (payload) => ({
  type: SET_BULK_STANDARDS_STATE,
  payload,
})

export const setBulkItemStatusAction = (payload) => ({
  type: SET_BULK_ITEM_STATUS,
  payload,
})

export const setBulkSuggestionsAction = (payload) => ({
  type: SET_BULK_SUGGESTIONS,
  payload,
})

export const updateBulkSuggestionAction = (payload) => ({
  type: UPDATE_BULK_SUGGESTION,
  payload,
})

export const saveItemStandardAction = (payload) => ({
  type: SAVE_ITEM_STANDARD,
  payload,
})

export const saveAllItemStandardsAction = () => ({
  type: SAVE_ALL_ITEM_STANDARDS,
})

export const clearBulkItemAction = (payload) => ({
  type: CLEAR_BULK_ITEM,
  payload,
})

export const resetBulkStandardsStateAction = () => ({
  type: RESET_BULK_STANDARDS_STATE,
})

// selectors
export const stateSelector = (state) => state?.[namespaceKey]

export const getBulkStandardsStateSelector = createSelector(
  stateSelector,
  (state) => state || {}
)

export const getBulkApiStatusSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.apiStatus || null
)

export const getBulkItemStatusSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.itemStatus || {}
)

export const getBulkSuggestionsSelector = createSelector(
  getBulkStandardsStateSelector,
  (state) => state?.suggestionsByQuestionId || {}
)

export const getEligibleCountSelector = createSelector(
  getTestItemsSelector,
  getUserIdSelector,
  (testItems, userId) =>
    getEligibleTestMetadataQuestions(testItems, userId).length
)

export const getPendingSuggestionsSelector = createSelector(
  getBulkItemStatusSelector,
  getBulkSuggestionsSelector,
  getTestItemsSelector,
  (itemStatus, suggestionsByQuestionId, testItems) =>
    Object.entries(itemStatus)
      .filter(([, status]) => status === BULK_ITEM_STATUS.SUCCESS)
      .map(([itemId]) => {
        const item = (testItems || []).find((it) => it._id === itemId)
        const questions = get(item, 'data.questions', [])
        const questionIds = questions
          .map((q) => q.id)
          .filter(
            (qId) =>
              suggestionsByQuestionId[qId]?.standard ||
              suggestionsByQuestionId[qId]?.standards?.length
          )
        return { itemId, questionIds }
      })
      .filter(({ questionIds }) => questionIds.length > 0)
)

// initial state
const initialState = {
  selectedGrade: null,
  selectedSubject: null,
  selectedCurriculumId: null,
  apiStatus: null,
  itemStatus: {},
  suggestionsByQuestionId: {},
  errorMessage: null,
}

// reducer
export const reducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case SET_BULK_STANDARDS_SELECTION:
      return { ...state, ...payload }
    case SET_BULK_STANDARDS_STATE:
      return { ...state, ...payload }
    case SET_BULK_ITEM_STATUS: {
      const { itemIds = [], status } = payload || {}
      const itemStatus = { ...state.itemStatus }
      itemIds.forEach((id) => {
        itemStatus[id] = status
      })
      return { ...state, itemStatus }
    }
    case SET_BULK_SUGGESTIONS:
      return {
        ...state,
        suggestionsByQuestionId: {
          ...state.suggestionsByQuestionId,
          ...(payload || {}),
        },
      }
    case UPDATE_BULK_SUGGESTION: {
      const { questionId, updates = {} } = payload || {}
      if (!questionId) return state
      return {
        ...state,
        suggestionsByQuestionId: {
          ...state.suggestionsByQuestionId,
          [questionId]: {
            ...(state.suggestionsByQuestionId?.[questionId] || {}),
            ...updates,
          },
        },
      }
    }
    case CLEAR_BULK_ITEM: {
      const { itemId, questionIds = [] } = payload || {}
      const itemStatus = { ...state.itemStatus }
      delete itemStatus[itemId]
      const suggestionsByQuestionId = { ...state.suggestionsByQuestionId }
      questionIds.forEach((qId) => {
        delete suggestionsByQuestionId[qId]
      })
      const apiStatus = Object.keys(itemStatus).length ? state.apiStatus : null
      return { ...state, itemStatus, suggestionsByQuestionId, apiStatus }
    }
    case RESET_BULK_STANDARDS_STATE:
      return {
        ...initialState,
      }
    default:
      return state
  }
}

/**
 * Resolves the effective standards to save for a suggestion.
 * Prefers the user-edited `standards` array (set via the metadata editor),
 * falling back to the single AI-suggested `standard`.
 * @param {Object} suggestion - { standard, standards, dok, difficulty }
 * @returns {Array} standards array (may be empty)
 */
export const getEffectiveStandards = (suggestion = {}) => {
  if (Array.isArray(suggestion?.standards)) {
    return suggestion.standards.filter(Boolean)
  }
  return suggestion?.standard ? [suggestion.standard] : []
}

// sagas
function* runBatchSaga({ batch, subject, grade, curriculumId }) {
  const batchItemIds = uniq(batch.map((q) => q.itemId))
  try {
    const results = yield call(aiAPI.generateQuestionMetadataBulk, {
      questions: batch.map(({ questionId, payload }) => ({
        questionId,
        ...payload,
      })),
      subject,
      ...(grade ? { grades: [grade] } : {}),
      ...(curriculumId ? { curriculumId } : {}),
    })

    const suggestions = {}
    ;(results || []).forEach((r) => {
      if (r?.questionId) {
        suggestions[r.questionId] = {
          standard: r.standard || null,
          dok: r.dok ?? null,
          difficulty: r.difficulty ?? null,
          confidence_score: r.confidence_score ?? null,
        }
      }
    })

    yield put(setBulkSuggestionsAction(suggestions))
    yield put(
      setBulkItemStatusAction({
        itemIds: batchItemIds,
        status: BULK_ITEM_STATUS.SUCCESS,
      })
    )
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: batchItemIds,
        status: BULK_ITEM_STATUS.ERROR,
      })
    )
  }
}

export function* generateBulkStandardsSaga({ payload }) {
  const { grade = null, subject = null, curriculumId = null } = payload || {}

  if (!subject) {
    notification({
      type: 'warning',
      msg: 'Please select a standard set to generate metadata',
    })
    return
  }

  try {
    const testItems = yield select(getTestItemsSelector)
    const userId = yield select(getUserIdSelector)
    const eligible = getEligibleTestMetadataQuestions(testItems, userId)

    if (!eligible.length) {
      notification({
        type: 'info',
        msg: 'No eligible questions are missing metadata',
      })
      yield put(resetBulkStandardsStateAction())
      return
    }

    const affectedItemIds = uniq(eligible.map((q) => q.itemId))
    const initialItemStatus = affectedItemIds.reduce((acc, id) => {
      acc[id] = BULK_ITEM_STATUS.GENERATING
      return acc
    }, {})

    yield put(
      setBulkStandardsStateAction({
        apiStatus: 'INITIATED',
        selectedGrade: grade,
        selectedSubject: subject,
        selectedCurriculumId: curriculumId,
        itemStatus: initialItemStatus,
        suggestionsByQuestionId: {},
        errorMessage: null,
      })
    )

    const batches = chunk(eligible, BULK_METADATA_BATCH_SIZE)

    yield all(
      batches.map((batch) =>
        call(runBatchSaga, { batch, subject, grade, curriculumId })
      )
    )

    yield put(setBulkStandardsStateAction({ apiStatus: 'SUCCESS' }))
  } catch (err) {
    yield put(
      setBulkStandardsStateAction({
        apiStatus: 'FAILED',
        errorMessage: 'Failed to generate metadata suggestions',
      })
    )
  }
}

export function* saveItemStandardSaga({ payload }) {
  const { itemId, questionIds = [] } = payload || {}
  try {
    const test = yield select(getTestEntitySelector)

    if (test?.status !== statusConstants.DRAFT) {
      yield put(clearBulkItemAction({ itemId, questionIds }))
      return
    }

    const allCurriculums = yield select(getCurriculumsListSelector)
    const allSuggestions = yield select(getBulkSuggestionsSelector)

    const itemSuggestions = questionIds.reduce((acc, qId) => {
      if (allSuggestions[qId]) acc[qId] = allSuggestions[qId]
      return acc
    }, {})

    const item = (test.itemGroups || [])
      .flatMap((group) => group.items || [])
      .find((it) => it && typeof it !== 'string' && it._id === itemId)

    if (!item) {
      yield put(clearBulkItemAction({ itemId, questionIds }))
      return
    }

    const questions = get(item, 'data.questions', [])
    const updatedQuestions = questions.map((q) => {
      const suggestion = itemSuggestions[q.id]
      const effectiveStandards = getEffectiveStandards(suggestion)
      const questionWithStandard = effectiveStandards.length
        ? applyAIMetadataToQuestion({
            question: q,
            metadata: {
              standards: effectiveStandards,
              dok: suggestion?.dok,
              difficulty: suggestion?.difficulty,
            },
            allCurriculums,
            dictAlignments: [],
          })
        : q
      return omit(questionWithStandard, QUESTION_SAVE_FIELDS_TO_OMIT)
    })

    const updatedItem = buildUpdatedItemForSave(item, updatedQuestions)

    yield put(
      setBulkItemStatusAction({
        itemIds: [itemId],
        status: BULK_ITEM_STATUS.SAVING,
      })
    )

    yield call(testItemsApi.updateById, itemId, updatedItem, test._id)
    yield put(clearBulkItemAction({ itemId, questionIds }))
    yield put(receiveTestByIdAction(test._id, true, false))

    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:SaveSuggestionsClick',
      {
        testId: test._id,
        itemId,
      },
      { attachUserData: true }
    )

    notification({
      type: 'success',
      msg: 'Suggestions saved successfully',
    })
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: [itemId],
        status: BULK_ITEM_STATUS.SUCCESS,
      })
    )
    notification({
      type: 'error',
      msg: 'Failed to save metadata suggestion',
    })
  }
}

export function* saveAllItemStandardsSaga() {
  const pending = yield select(getPendingSuggestionsSelector)
  if (!pending.length) return

  const affectedItemIds = pending.map((p) => p.itemId)
  try {
    const test = yield select(getTestEntitySelector)

    if (test?.status !== statusConstants.DRAFT) {
      yield put(resetBulkStandardsStateAction())
      return
    }

    const allCurriculums = yield select(getCurriculumsListSelector)
    const allSuggestions = yield select(getBulkSuggestionsSelector)

    const itemsById = (test.itemGroups || [])
      .flatMap((group) => group.items || [])
      .reduce((acc, it) => {
        if (it && typeof it !== 'string') acc[it._id] = it
        return acc
      }, {})

    const itemsToUpdate = pending
      .map(({ itemId, questionIds }) => {
        const item = itemsById[itemId]
        if (!item) return null
        const itemSuggestions = questionIds.reduce((acc, qId) => {
          if (allSuggestions[qId]) acc[qId] = allSuggestions[qId]
          return acc
        }, {})
        const questions = get(item, 'data.questions', [])
        const updatedQuestions = questions.map((q) => {
          const suggestion = itemSuggestions[q.id]
          const effectiveStandards = getEffectiveStandards(suggestion)
          const questionWithStandard = effectiveStandards.length
            ? applyAIMetadataToQuestion({
                question: q,
                metadata: {
                  standards: effectiveStandards,
                  dok: suggestion?.dok,
                  difficulty: suggestion?.difficulty,
                },
                allCurriculums,
                dictAlignments: [],
              })
            : q
          return omit(questionWithStandard, QUESTION_SAVE_FIELDS_TO_OMIT)
        })
        return {
          itemId,
          updatedItem: buildUpdatedItemForSave(item, updatedQuestions),
        }
      })
      .filter(Boolean)

    if (!itemsToUpdate.length) {
      yield put(resetBulkStandardsStateAction())
      return
    }

    yield put(
      setBulkItemStatusAction({
        itemIds: affectedItemIds,
        status: BULK_ITEM_STATUS.SAVING,
      })
    )

    yield all(
      itemsToUpdate.map(({ itemId, updatedItem }) =>
        call(testItemsApi.updateById, itemId, updatedItem, test._id)
      )
    )

    yield put(resetBulkStandardsStateAction())
    yield put(receiveTestByIdAction(test._id, true, false))

    notification({
      type: 'success',
      msg: 'Suggestions saved successfully',
    })
  } catch (err) {
    yield put(
      setBulkItemStatusAction({
        itemIds: affectedItemIds,
        status: BULK_ITEM_STATUS.SUCCESS,
      })
    )
    notification({
      type: 'error',
      msg: 'Failed to save metadata suggestions',
    })
  }
}

export function* watcherSaga() {
  yield all([
    takeLatest(GENERATE_BULK_STANDARDS, generateBulkStandardsSaga),
    takeEvery(SAVE_ITEM_STANDARD, saveItemStandardSaga),
    takeLatest(SAVE_ALL_ITEM_STANDARDS, saveAllItemStandardsSaga),
  ])
}
